import Renew16 from '@carbon/icons-react/es/renew/16';
import { ColDef } from 'ag-grid-community';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UploadFileWithProgress } from 'api/project-files/types';
import { Collapse, Panel } from 'components/atoms/collapse';
import { Grid } from 'components/atoms/grid';
import { Loading } from 'components/atoms/loading';
import { formatBytes } from 'utils/files';

const GridWrapper = styled.div`
  margin-top: var(--layout-01);

  .ag-layout-auto-height .ag-center-cols-clipper {
    min-height: 80px;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  color: var(--gray-80);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-05);

  svg {
    margin-right: var(--spacing-04);
  }
`;

const UploadProgressGrid = styled(Grid)`
  .progress-bar {
    height: 3px;
    transition: width 1s;
    background: var(--primary-color);
  }

  .ag-react-container > :not(:first-child) {
    display: none !important;
  }
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const FileName = styled.div`
  margin-left: var(--spacing-05);
`;

const FileNameRenderer = ({ data }) => {
  return (
    <FileNameCell>
      <Loading size="small" />
      <FileName>{data.name}</FileName>
    </FileNameCell>
  );
};

const GridCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding-right: 0 !important;
  }

  .ant-collapse-item-active {
    padding-bottom: 0 !important;
  }
`;

export const ProjectFilesUploading = ({
  files,
}: {
  files: { [id: number]: UploadFileWithProgress };
}) => {
  const [rowData, setRowData] = useState<any[]>([]);
  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: 'name', flex: 1, cellRenderer: 'fileNameRenderer' },
      { field: 'size', width: 200 },
      { field: 'group', width: 200 },
      { width: 48 },
    ],
    [],
  );

  useEffect(() => {
    const uploadData: any[] = [];

    Object.values(files).forEach((_file) => {
      uploadData.push({
        ..._file,
        id: _file.uid,
        size: formatBytes(_file.size, 1),
      });
    });

    setRowData(uploadData);
  }, [files]);

  const frameworkComponents = useMemo(
    () => ({
      fileNameRenderer: memo(FileNameRenderer),
    }),
    [],
  );

  const defaultColumnDef = useMemo(
    () => ({
      suppressMenu: true,
    }),
    [],
  );

  if (!files || Object.keys(files).length <= 0) {
    return null;
  }

  return (
    <GridWrapper>
      <GridCollapse defaultActiveKey="files-uploading" expandIcon={() => <></>}>
        <Panel
          key="files-uploading"
          header={
            <Title>
              <Renew16 />
              <FormattedMessage
                id="project.files.uploading.title"
                values={{
                  count: Object.keys(files).length,
                }}
              />
            </Title>
          }
        >
          <UploadProgressGrid
            className="ag-theme-alpine"
            columnDefs={columnDefs}
            rowData={rowData}
            suppressContextMenu
            suppressCellSelection
            suppressHorizontalScroll
            domLayout="autoHeight"
            frameworkComponents={frameworkComponents}
            animateRows
            defaultColDef={defaultColumnDef}
          />
        </Panel>
      </GridCollapse>
    </GridWrapper>
  );
};
